import { useState, type ReactNode } from 'react';
import AccountBalanceRoundedIcon from '@mui/icons-material/AccountBalanceRounded';
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded';
import CompareArrowsRoundedIcon from '@mui/icons-material/CompareArrowsRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import RemoveCircleOutlineRoundedIcon from '@mui/icons-material/RemoveCircleOutlineRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Button, Chip, Collapse, Divider, Stack, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type {
  CombinedScoreViewModel,
  LoanReviewViewModel,
  ScoreContribution,
  SourceCardSubCheck,
  SourceCardViewModel,
} from '../../lib/loanReview/mapLoanReviewToViewModel';
import { displayStatusLabel } from '../../lib/loanReview/mapLoanReviewToViewModel';
import { statusChipColor } from './loanReviewChipColors';
import { availableAnchors, hasAnchor, scrollToDomain, sourceCardAnchorId } from './loanReviewAnchors';
import { ADJUSTER_SKIP_REASON } from '../../lib/loanReview/mapLoanReviewToViewModel';
import { capExplanation, capRowLabel, isVisiblyCapped } from '../../lib/loanReview/scoreCap';
import { ApplicationSectionCard } from '../application/applicationSectionChrome';

const SOURCE_ICON: Record<string, ReactNode> = {
  identity: <BadgeRoundedIcon fontSize="small" />,
  income: <PaymentsRoundedIcon fontSize="small" />,
  employer: <BusinessRoundedIcon fontSize="small" />,
  decision_engine: <AccountBalanceRoundedIcon fontSize="small" />,
  cross_check: <CompareArrowsRoundedIcon fontSize="small" />,
};

function subCheckIcon(status: string) {
  const s = status.toUpperCase();
  if (s === 'PASS') return <CheckCircleRoundedIcon fontSize="small" color="success" />;
  if (s === 'PASS_WITH_VARIATION' || s === 'REVIEW')
    return <WarningAmberRoundedIcon fontSize="small" color="warning" />;
  if (s === 'FAIL' || s === 'ERROR') return <CancelRoundedIcon fontSize="small" color="error" />;
  return <RemoveCircleOutlineRoundedIcon fontSize="small" sx={{ color: 'text.disabled' }} />;
}

/** Fill % = how well this check scored (100 = pass, 50 = review, 0 = fail). */
function fillPct(contribution: number, weight: number): number {
  if (weight <= 0) return 0;
  return Math.max(0, Math.min(100, (contribution / weight) * 100));
}

function ScoreBar({ pct }: { pct: number }) {
  const color = pct >= 99 ? 'success.main' : pct <= 1 ? 'error.main' : 'warning.main';
  return (
    <Box
      sx={(t) => ({
        height: 6,
        borderRadius: 3,
        bgcolor: alpha(t.palette.text.primary, 0.08),
        overflow: 'hidden',
      })}
    >
      <Box sx={{ height: '100%', width: `${pct}%`, bgcolor: color, borderRadius: 3, transition: 'width 200ms' }} />
    </Box>
  );
}

interface BreakdownItem {
  label: string;
  status: string;
  weight: number;
  contribution: number;
}

function wordColor(status: string): string {
  const s = status.toUpperCase();
  if (s === 'PASS') return 'success.main';
  if (s === 'PASS_WITH_VARIATION' || s === 'REVIEW') return 'warning.main';
  if (s === 'FAIL' || s === 'ERROR' || s === 'UNAVAILABLE') return 'error.main';
  return 'text.secondary';
}

/**
 * Shows how a score is built: each row is one area/check with the result in
 * words and the points it added out of the points it could ("8.3 of 17 pts").
 * The bold Total row is the only place a "/100" appears — the real score.
 */
function BreakdownTable({
  rows,
  totalLabel,
  totalValue,
  subtotalLabel,
  subtotalValue,
  footerRows,
}: {
  rows: BreakdownItem[];
  totalLabel: string;
  totalValue: number;
  /** Shown when the rows sum to something other than the total (see footerRows). */
  subtotalLabel?: string;
  subtotalValue?: number | null;
  /** Adjustments applied after the rows — each already signed. */
  footerRows?: Array<{ label: string; value: string; muted?: boolean }>;
}) {
  const totalWeight = rows.reduce((s, r) => s + r.weight, 0);
  return (
    <Stack spacing={1}>
      {rows.map((r) => {
        const maxPts = totalWeight > 0 ? (r.weight / totalWeight) * 100 : 0;
        const scorePct = fillPct(r.contribution, r.weight);
        const points = totalWeight > 0 ? (r.contribution / totalWeight) * 100 : 0;
        const reduced = scorePct < 99.5;
        // A full pass earns exactly its max — round both the same way so it can
        // never read "11.4 of 11". Only reductions show a decimal.
        const maxRounded = Math.round(maxPts);
        const earned = reduced ? Math.min(maxRounded, Math.round(points * 10) / 10) : maxRounded;
        const earnedLabel = Number.isInteger(earned) ? String(earned) : earned.toFixed(1);
        return (
          <Stack key={r.label} spacing={0.3}>
            <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={1}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {r.label}
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 700 }} color={wordColor(r.status)}>
                {displayStatusLabel(r.status)}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <Box sx={{ flex: 1 }}>
                <ScoreBar pct={scorePct} />
              </Box>
              <Typography
                variant="caption"
                sx={{ minWidth: 92, textAlign: 'right', fontWeight: 700 }}
                color={reduced ? 'warning.main' : 'text.secondary'}
              >
                {earnedLabel} of {maxRounded} pts
              </Typography>
            </Stack>
          </Stack>
        );
      })}
      {footerRows?.length ? (
        <>
          <Divider sx={{ my: 0.25 }} />
          {subtotalValue != null ? (
            <Stack direction="row" justifyContent="space-between" alignItems="baseline">
              <Typography variant="caption" sx={{ fontWeight: 700 }}>
                {subtotalLabel}
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                {subtotalValue.toFixed(1)}
              </Typography>
            </Stack>
          ) : null}
          {footerRows.map((f) => (
            <Stack key={f.label} direction="row" justifyContent="space-between" alignItems="baseline">
              <Typography variant="caption" color="text.secondary">
                {f.label}
              </Typography>
              <Typography
                variant="caption"
                color={f.muted ? 'text.disabled' : 'warning.main'}
                sx={{ fontWeight: f.muted ? 400 : 700, fontVariantNumeric: 'tabular-nums' }}
              >
                {f.value}
              </Typography>
            </Stack>
          ))}
        </>
      ) : null}
      <Divider sx={{ my: 0.25 }} />
      <Stack direction="row" justifyContent="space-between" alignItems="baseline">
        <Typography variant="body2" sx={{ fontWeight: 800 }}>
          {totalLabel}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 800 }}>
          {Math.round(totalValue)}/100
        </Typography>
      </Stack>
    </Stack>
  );
}

function ScoreExplainer({
  toggleLabel,
  intro,
  children,
  testId,
}: {
  toggleLabel: string;
  intro: string;
  children: ReactNode;
  testId: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Box data-testid={testId}>
      <Button
        size="small"
        variant="text"
        onClick={() => setOpen((v) => !v)}
        endIcon={
          <ExpandMoreRoundedIcon
            fontSize="small"
            sx={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }}
          />
        }
        sx={{ px: 0.5, textTransform: 'none', fontWeight: 700 }}
      >
        {toggleLabel}
      </Button>
      <Collapse in={open} unmountOnExit>
        <Stack spacing={1} sx={{ pt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {intro}
          </Typography>
          {children}
        </Stack>
      </Collapse>
    </Box>
  );
}

function sumPoints(rows: BreakdownItem[]): number {
  const totalWeight = rows.reduce((s, r) => s + r.weight, 0);
  if (totalWeight <= 0) return 0;
  return (rows.reduce((s, r) => s + r.contribution, 0) / totalWeight) * 100;
}

/**
 * The card's score is the one the final score counted. When a status cap lowered it,
 * the uncapped figure is shown beside it — the same "(capped from N)" form as the
 * summary chip — so 50 here and 100 in the checks below read as one fact with a reason.
 */
function CardScoreChip({
  label,
  score,
  internalScore,
  status,
  capChecks,
}: {
  label: string;
  score: number;
  internalScore: number | null;
  status: string;
  /** Which checks held the score down — named in the tooltip. */
  capChecks: string[];
}) {
  const capped = isVisiblyCapped(internalScore, score);
  const chip = (
    <Chip
      size="small"
      variant="outlined"
      label={capped ? `Score ${Math.round(score)} (capped from ${Math.round(internalScore!)})` : `Score ${Math.round(score)}`}
      sx={{ fontWeight: 700, height: 22 }}
      data-testid="loan-review-card-score-chip"
    />
  );
  return capped ? (
    <Tooltip arrow title={capExplanation(label, internalScore!, score, status, capChecks)}>
      {chip}
    </Tooltip>
  ) : (
    chip
  );
}

function CardScoreExplainer({
  rows,
  total,
  internalScore,
  status,
  capChecks,
}: {
  rows: ScoreContribution[];
  total: number | null;
  /** The rows' own sum, before a status cap. See SourceCardViewModel.internalScore. */
  internalScore: number | null;
  /** The card's status — the cap's reason is worded from it. */
  status: string;
  capChecks: string[];
}) {
  if (!rows.length) return null;
  const items: BreakdownItem[] = rows.map((r) => ({ label: r.label, status: r.status, weight: r.weight, contribution: r.contribution }));
  const ordered = [...items].sort((a, b) => fillPct(a.contribution, a.weight) - fillPct(b.contribution, b.weight));

  // A status like REVIEW can cap this area's contribution without touching any
  // scored sub-check (e.g. gambling, which carries no weight of its own) -
  // without this, the rows would sum to 100 under a total that reads 50, with
  // nothing in the table explaining the gap.
  const capped = isVisiblyCapped(internalScore, total);
  const footerRows = capped
    ? [{ label: capRowLabel(status, capChecks), value: `−${(internalScore! - total!).toFixed(1)}` }]
    : undefined;

  return (
    <ScoreExplainer
      toggleLabel="How this score was calculated"
      intro="Each check can add up to a set number of points (its share of this area). It adds the full amount when it passes, and less when it needs review — the area score is the sum."
      testId="loan-review-card-score-explainer"
    >
      <BreakdownTable
        rows={ordered}
        subtotalLabel={capped ? 'Checks alone' : undefined}
        subtotalValue={capped ? internalScore : undefined}
        footerRows={footerRows}
        totalLabel="Area score"
        totalValue={total ?? sumPoints(items)}
      />
    </ScoreExplainer>
  );
}

function CombinedScoreExplainer({ combined }: { combined: CombinedScoreViewModel }) {
  const rows = combined.breakdown;
  if (!rows.length) return null;
  const items: BreakdownItem[] = rows.map((r) => ({
    label: r.label,
    status: r.status,
    weight: r.weight,
    contribution: r.contribution,
  }));
  const ordered = [...items].sort((a, b) => fillPct(a.contribution, a.weight) - fillPct(b.contribution, b.weight));

  // The domain rows sum to the AGENT 1 score, not the final one. Without these
  // footer rows the table would show five rows adding to ~99 under a total of ~93,
  // and the missing points would be unaccounted for.
  const footerRows = (combined.adjustments ?? []).map((a) => ({
    label: a.counted && a.subscore != null ? `${a.label} (scored ${Math.round(a.subscore)}/100)` : a.label,
    value: a.counted
      ? `${a.adjustment >= 0 ? '+' : '−'}${Math.abs(a.adjustment).toFixed(1)}`
      : ADJUSTER_SKIP_REASON[a.reason ?? ''] ?? 'no effect',
    muted: !a.counted,
  }));

  return (
    <ScoreExplainer
      toggleLabel="How the final score was calculated"
      intro="Each area can add up to a set number of points out of 100 (its weight): the full amount when it passes, less when it needs review. Together they are Agent 1 — what our own verification of the applicant found. Outside signals are then applied to that number, each shown as the points it moved."
      testId="loan-review-combined-explainer"
    >
      <BreakdownTable
        rows={ordered}
        subtotalLabel="Agent 1 — internal evidence"
        subtotalValue={combined.agent1Score}
        footerRows={footerRows}
        totalLabel="Final score"
        totalValue={combined.score ?? sumPoints(items)}
      />
    </ScoreExplainer>
  );
}

/** Cross-check style row: what we did + the two values that differ. */
function SubCheckRow({ check }: { check: SourceCardSubCheck }) {
  const showValues =
    check.status.toUpperCase() !== 'PASS' && Boolean(check.idValue || check.otherValue);
  return (
    <Stack direction="row" spacing={1} alignItems="flex-start">
      <Box sx={{ mt: '2px', flexShrink: 0 }}>{subCheckIcon(check.status)}</Box>
      <Box sx={{ minWidth: 0 }}>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
            {check.plain}
          </Typography>
          {check.hint ? (
            <Tooltip arrow title={check.hint}>
              <InfoOutlinedIcon
                sx={{ fontSize: 14, color: 'text.disabled', cursor: 'help', flexShrink: 0 }}
                aria-label={check.hint}
              />
            </Tooltip>
          ) : null}
        </Stack>
        {showValues ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.15 }}>
            {check.field ? `${check.field} — ` : ''}
            {check.idValue ? <>ID: <b>{check.idValue}</b></> : null}
            {check.idValue && check.otherValue ? '  ·  ' : ''}
            {check.otherValue ? (
              <>
                {check.otherSource ?? 'Other'}: <b>{check.otherValue}</b>
              </>
            ) : null}
          </Typography>
        ) : null}
      </Box>
    </Stack>
  );
}

function SourceCard({ card }: { card: SourceCardViewModel }) {
  const notRun = card.status.toUpperCase() === 'NOT_RUN';
  return (
    <ApplicationSectionCard
      title={card.signal}
      subtitle={card.source || undefined}
      icon={SOURCE_ICON[card.key] ?? <InsightsRoundedIcon fontSize="small" />}
      id={sourceCardAnchorId(card.key)}
      data-testid={`loan-review-source-card-${card.key}`}
      endAdornment={
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          <Chip
            size="small"
            color={notRun ? 'default' : statusChipColor(card.status)}
            label={notRun ? 'Not run' : displayStatusLabel(card.status)}
            sx={{ fontWeight: 700, height: 22 }}
          />
          {card.weightedScore != null ? (
            <CardScoreChip
              label={card.signal}
              score={card.weightedScore}
              internalScore={card.internalScore}
              status={card.status}
              capChecks={card.capChecks}
            />
          ) : null}
        </Stack>
      }
    >
      <Stack spacing={1.25}>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.45 }}>
          {card.summary}
        </Typography>

        {card.received.length ? (
          <Box>
            <Typography variant="caption" sx={{ fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'text.secondary' }}>
              What we received
            </Typography>
            <Box component="dl" sx={{ m: 0, mt: 0.25 }}>
              {card.received.map((r, i) => (
                <Box key={r.label}>
                  {i > 0 ? <Divider /> : null}
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: 'minmax(110px, 140px) 1fr' },
                      columnGap: 1.25,
                      py: 0.5,
                      alignItems: 'baseline',
                    }}
                  >
                    <Typography component="dt" sx={{ fontSize: '0.72rem', fontWeight: 600, color: 'text.secondary' }}>
                      {r.label}
                    </Typography>
                    <Box component="dd" sx={{ m: 0, fontSize: '0.875rem', fontWeight: 500, wordBreak: 'break-word' }}>
                      {r.value}
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        ) : null}

        {card.subChecks.length ? (
          <Box>
            <Typography variant="caption" sx={{ fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'text.secondary' }}>
              What we did
            </Typography>
            <Stack spacing={0.75} sx={{ mt: 0.5 }}>
              {card.subChecks.map((sc) => (
                <SubCheckRow key={sc.name} check={sc} />
              ))}
            </Stack>
          </Box>
        ) : null}

        <CardScoreExplainer
          rows={card.scoreBreakdown}
          total={card.weightedScore}
          internalScore={card.internalScore}
          status={card.status}
          capChecks={card.capChecks}
        />
      </Stack>
    </ApplicationSectionCard>
  );
}

const LANE_LABEL: Record<string, string> = {
  APPROVE: 'Approved',
  REVIEW: 'Needs review',
  REJECT: 'Not eligible',
  FOLLOW_PROVENIR: 'Follow credit decision',
};

function laneColor(lane: string): 'default' | 'success' | 'warning' | 'error' {
  const l = lane.toUpperCase();
  if (l === 'APPROVE') return 'success';
  if (l === 'REVIEW') return 'warning';
  if (l === 'REJECT' || l === 'FOLLOW_PROVENIR') return 'error';
  return 'default';
}

function capitalize(s: string): string {
  const t = s.trim();
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : t;
}

/**
 * True when the lane came from the score band itself (not an explicit hard-stop
 * or review-floor rule) — the only case where "biggest drags" adds information,
 * since a hard-stop / review-floor reason already names the specific cause.
 * Matches the backend's decision_basis text: score-driven paths start with
 * "score …"; hard-stop/follow-Provenir start with "hard stop:"; the review
 * floor starts with "human review required —".
 */
function isScoreDriven(basis: string | null): boolean {
  return Boolean(basis && basis.trim().toLowerCase().startsWith('score'));
}

function laneExplain(lane: string): string {
  switch (lane.toUpperCase()) {
    case 'APPROVE':
      return 'Cleared automated checks — eligible for the auto-funding path.';
    case 'REVIEW':
      return 'Sent for human review — the combined score is below the auto-approve line.';
    case 'REJECT':
      return 'Not eligible for auto-approval — routed to a credit/decline review (never an automatic decline).';
    case 'FOLLOW_PROVENIR':
      return 'Follows the credit decision — the bureau/Provenir result was not an approval.';
    default:
      return '';
  }
}

function CombinedHeader({
  combined,
  anchors,
}: {
  combined: CombinedScoreViewModel;
  anchors: ReadonlySet<string>;
}) {
  // The lane is the decision (Approve/Review/Reject); the score is the quality behind it.
  const lane = (combined.lane || combined.status || 'APPROVE').toUpperCase();
  const label = LANE_LABEL[lane] ?? displayStatusLabel(combined.status || lane);
  const why = combined.decisionBasis ? capitalize(combined.decisionBasis) : laneExplain(lane);
  return (
    <ApplicationSectionCard
      title="Final result"
      subtitle="Agent 1's verification, adjusted by outside signals"
      icon={<InsightsRoundedIcon fontSize="small" />}
      data-testid="loan-review-combined"
      endAdornment={
        <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
          {combined.score != null ? (
            <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1 }}>
              {Math.round(combined.score)}
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.25 }}>
                /100
              </Typography>
            </Typography>
          ) : null}
          <Chip size="small" color={laneColor(lane)} label={label} sx={{ fontWeight: 700, height: 22 }} />
        </Stack>
      }
    >
      <Stack spacing={1.25}>
        <Typography variant="body2" sx={{ lineHeight: 1.45, fontWeight: 600 }}>
          {why}
        </Typography>
        {(lane === 'REVIEW' || lane === 'REJECT') &&
        isScoreDriven(combined.decisionBasis) &&
        combined.topDrags.length ? (
          <Typography variant="caption" color="text.secondary">
            Biggest drags: {combined.topDrags.slice(0, 3).map((d) => `${d.label} −${d.pointsLost.toFixed(1)}`).join(', ')}
          </Typography>
        ) : null}
        {combined.domainScores.length ? (
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            {[
              ...combined.domainScores.map((d) => ({
                key: d.domain,
                // Both numbers when they differ. The counted score leads, because that
                // is what the total is built from, and the card score follows so the
                // card's own "Score 50 (capped from 82)" chip reads as the same fact,
                // in the same words, rather than two numbers that disagree.
                label:
                  d.cappedByStatus && isVisiblyCapped(d.cardScore, d.score)
                    ? `${d.label}: ${Math.round(d.score)} (capped from ${Math.round(d.cardScore!)})`
                    : `${d.label}: ${Math.round(d.score)}`,
                title:
                  d.cappedByStatus && isVisiblyCapped(d.cardScore, d.score)
                    ? capExplanation(d.label, d.cardScore!, d.score, d.status, d.capChecks)
                    : undefined,
                adjuster: false,
              })),
              // Counted adjusters ride in the same row: they are part of the score
              // the card headlines, and a reviewer should be able to reach the
              // evidence behind a deduction the same way as any other area.
              ...(combined.adjustments ?? [])
                .filter((a) => a.counted && a.subscore != null)
                .map((a) => ({
                  key: a.adjuster,
                  label: `${a.label}: ${Math.round(a.subscore!)} (${
                    a.adjustment >= 0 ? '+' : '−'
                  }${Math.abs(a.adjustment).toFixed(1)})`,
                  title: undefined as string | undefined,
                  adjuster: true,
                })),
            ].map((c) => {
              // Only clickable when the destination is actually on the page. A chip
              // that looks like a button and does nothing is worse than a plain one.
              const jumpable = hasAnchor(c.key, anchors);
              return (
                <Chip
                  key={c.key || c.label}
                  size="small"
                  variant="outlined"
                  color={c.adjuster ? 'warning' : 'default'}
                  label={c.label}
                  title={c.title}
                  clickable={jumpable}
                  onClick={jumpable ? () => scrollToDomain(c.key) : undefined}
                  aria-label={jumpable ? `Go to ${c.label}` : undefined}
                  sx={{
                    fontWeight: 600,
                    height: 22,
                    ...(jumpable
                      ? {
                          cursor: 'pointer',
                          '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
                        }
                      : null),
                  }}
                />
              );
            })}
          </Stack>
        ) : null}
        <CombinedScoreExplainer combined={combined} />
      </Stack>
    </ApplicationSectionCard>
  );
}

/** Panel: combined score + one card per source (received → what we did → status). */
export function LoanReviewSourceCards({ vm }: { vm: LoanReviewViewModel }) {
  if (!vm.sourceCards.length && !vm.combined) return null;

  // Which combined-result chips can offer a jump. Computed from the view model,
  // not the DOM — the targets mount in this same commit.
  const anchors = availableAnchors(vm);

  // Cross-check carries the most sub-checks → give it the full width.
  const primaryCards = vm.sourceCards.filter((c) => c.key !== 'cross_check');
  const crossCard = vm.sourceCards.find((c) => c.key === 'cross_check');

  return (
    <Stack spacing={2} data-testid="loan-review-source-cards">
      {vm.combined ? <CombinedHeader combined={vm.combined} anchors={anchors} /> : null}
      {primaryCards.length ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
            gap: 2,
          }}
        >
          {primaryCards.map((card) => (
            <SourceCard key={card.key} card={card} />
          ))}
        </Box>
      ) : null}
      {crossCard ? <SourceCard card={crossCard} /> : null}
    </Stack>
  );
}
